
import { useState, useCallback } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Reward, RewardTriggerSource, RewardMachineType } from '@/components/rewards/types';
import { showRewardNotification } from '@/components/rewards/RewardNotification';

export const useRewardSystem = () => {
  const { user } = useAuth();
  const [isSlotMachineOpen, setIsSlotMachineOpen] = useState(false);
  const [isMysteryBoxOpen, setIsMysteryBoxOpen] = useState(false);
  const [currentTriggerSource, setCurrentTriggerSource] = useState<RewardTriggerSource>('daily_login');

  const checkRewardEligibility = useCallback(async (triggerSource: RewardTriggerSource): Promise<boolean> => {
    if (!user) return false;

    try {
      // For data_entry triggers, add 20% chance
      if (triggerSource === 'data_entry') {
        return Math.random() < 0.2; // 20% chance
      }
      
      // For daily_login, streak, achievement - always eligible (will be checked in backend)
      return true;
    } catch (error) {
      console.error('Error checking reward eligibility:', error);
      return false;
    }
  }, [user]);

  const triggerReward = useCallback(async (
    triggerSource: RewardTriggerSource,
    machineType: RewardMachineType = 'slot_machine'
  ) => {
    // Fast-track eligibility check for better UX
    const isEligible = await checkRewardEligibility(triggerSource);
    if (!isEligible) return;

    setCurrentTriggerSource(triggerSource);
    
    // Open reward interface immediately for better perceived performance
    if (machineType === 'slot_machine') {
      setIsSlotMachineOpen(true);
    } else {
      setIsMysteryBoxOpen(true);
    }
  }, [checkRewardEligibility]);

  const onRewardClaimed = useCallback(async (reward: Reward) => {
    try {
      // Apply the reward to the database in background
      const applyRewardPromise = supabase.functions.invoke('apply-reward', {
        body: { reward }
      });

      // Show notification immediately for better UX
      showRewardNotification(reward);
      
      // Dispatch event immediately for UI updates
      window.dispatchEvent(new CustomEvent('rewardClaimed', { detail: reward }));
      
      // Handle database update in background
      const { error } = await applyRewardPromise;
      if (error) {
        console.error('Failed to apply reward:', error);
      } else {
        console.log('Successfully applied reward:', reward);
      }
    } catch (error) {
      console.error('Error applying reward:', error);
    }
  }, []);

  return {
    isSlotMachineOpen,
    isMysteryBoxOpen,
    currentTriggerSource,
    setIsSlotMachineOpen,
    setIsMysteryBoxOpen,
    triggerReward,
    onRewardClaimed,
    checkRewardEligibility
  };
};
